import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, RefreshCw, Loader2, Phone, Mail, MapPin, CreditCard, ReceiptText, Inbox, Trash2, Copy, Check } from "lucide-react";
import Seo from "@/components/Seo";
import { fetchMe, fetchLeads, fetchStats, clearToken, getToken, deleteLead } from "@/lib/auth";

function CopyField({ label, value, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch {}
  };
  return (
    <button onClick={copy} data-testid={testid} title={`Copy ${label}`}
      className="inline-flex items-center gap-1.5 font-mono-tech text-sm text-white hover:text-cyan-300 transition-colors">
      <span className="tabular-nums">{value}</span>
      {copied ? <Check size={13} className="text-emerald-400" /> : <Copy size={13} className="text-zinc-500" />}
    </button>
  );
}

const TABS = [
  { key: "", label: "All" },
  { key: "site_survey", label: "Site Surveys" },
  { key: "contact", label: "Contact" },
  { key: "consultation", label: "Consultations" },
  { key: "billing", label: "Billing" },
  { key: "payment", label: "Payments" },
  { key: "feedback", label: "Feedback" },
  { key: "business", label: "Business" },
];

const TYPE_LABEL = {
  site_survey: "Site Survey", contact: "Contact", consultation: "Consultation",
  billing: "Billing", payment: "Payment", feedback: "Feedback", business: "Business",
};
const TYPE_COLOR = {
  site_survey: "bg-cyan-500/15 text-cyan-300 border-cyan-500/30",
  contact: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  consultation: "bg-violet-500/15 text-violet-300 border-violet-500/30",
  billing: "bg-pink-500/15 text-pink-300 border-pink-500/30",
  payment: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  feedback: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  business: "bg-blue-500/15 text-blue-300 border-blue-500/30",
};

function fmtDate(iso) {
  try { return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
}

export default function Admin() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("");
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  const load = useCallback(async (type) => {
    setLoading(true);
    try {
      const [ls, st] = await Promise.all([fetchLeads(type), fetchStats()]);
      setLeads(ls);
      setStats(st);
    } catch (err) {
      if (err.response?.status === 401) { clearToken(); navigate("/admin/login"); }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!getToken()) { navigate("/admin/login"); return; }
    fetchMe().then(setUser).catch(() => { clearToken(); navigate("/admin/login"); });
  }, [navigate]);

  useEffect(() => { if (getToken()) load(tab); }, [tab, load]);

  const logout = () => { clearToken(); navigate("/admin/login"); };

  const [deletingId, setDeletingId] = useState(null);
  const handleDelete = async (l) => {
    if (!window.confirm(`Delete this ${TYPE_LABEL[l.type] || l.type} record from ${l.name}? This permanently removes any stored card data and cannot be undone.`)) return;
    setDeletingId(l.id);
    try {
      await deleteLead(l.id);
      setLeads((prev) => prev.filter((x) => x.id !== l.id));
      setStats((prev) => ({ ...prev, total: Math.max(0, (prev.total ?? 1) - 1), [l.type]: Math.max(0, (prev[l.type] ?? 1) - 1) }));
    } catch (err) {
      if (err.response?.status === 401) { clearToken(); navigate("/admin/login"); }
      else alert("Failed to delete record. Please try again.");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <>
      <Seo title="Admin Dashboard | CYAN Wireless" description="CYAN Wireless submissions." path="/admin" />
      <section className="min-h-screen bg-ink-950 pt-28 pb-20 px-5 sm:px-8">
        <div className="max-w-7xl mx-auto">
          {/* header */}
          <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
            <div>
              <p className="overline mb-2">Admin</p>
              <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-white">Form Submissions</h1>
              {user && <p className="text-zinc-500 text-sm mt-2">Signed in as {user.email}</p>}
            </div>
            <div className="flex gap-3">
              <button onClick={() => load(tab)} className="inline-flex items-center gap-2 border border-white/15 text-white text-sm px-4 py-2.5 rounded-full hover:border-cyan-500 hover:text-cyan-400 transition-colors" data-testid="admin-refresh">
                <RefreshCw size={15} /> Refresh
              </button>
              <button onClick={logout} className="inline-flex items-center gap-2 border border-white/15 text-white text-sm px-4 py-2.5 rounded-full hover:border-red-500 hover:text-red-400 transition-colors" data-testid="admin-logout">
                <LogOut size={15} /> Log Out
              </button>
            </div>
          </div>

          {/* stat tiles */}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 mb-8">
            {TABS.map((t) => {
              const count = t.key ? (stats[t.key] ?? 0) : (stats.total ?? 0);
              const active = tab === t.key;
              return (
                <button key={t.key || "all"} onClick={() => setTab(t.key)} data-testid={`admin-tab-${t.key || "all"}`}
                  className={`text-left rounded-2xl border p-4 transition-colors ${active ? "border-cyan-500 bg-cyan-500/10" : "border-white/10 bg-ink-900 hover:border-white/25"}`}>
                  <div className="font-display font-black text-3xl text-white">{count}</div>
                  <div className="font-mono-tech text-[10px] uppercase tracking-widest text-zinc-400 mt-1">{t.label}</div>
                </button>
              );
            })}
          </div>

          {/* list */}
          {loading ? (
            <div className="grid place-items-center py-24 text-cyan-400"><Loader2 className="animate-spin" size={30} /></div>
          ) : leads.length === 0 ? (
            <div className="grid place-items-center py-24 text-zinc-500"><Inbox size={30} className="mb-3" /> No submissions yet.</div>
          ) : (
            <div className="space-y-4" data-testid="admin-leads-list">
              {leads.map((l) => (
                <div key={l.id} className="bg-ink-900 border border-white/10 rounded-2xl p-6" data-testid="admin-lead-item">
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                    <div className="flex items-center gap-3">
                      <span className={`text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border ${TYPE_COLOR[l.type] || "bg-white/10 text-white border-white/20"}`}>{TYPE_LABEL[l.type] || l.type}</span>
                      <span className="font-display font-bold text-lg text-white">{l.name}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-mono-tech text-xs text-zinc-500">{fmtDate(l.created_at)}</span>
                      <button onClick={() => handleDelete(l)} disabled={deletingId === l.id} data-testid="admin-delete-lead"
                        className="inline-flex items-center gap-1.5 border border-white/10 text-zinc-400 text-xs px-3 py-1.5 rounded-full hover:border-red-500 hover:text-red-400 transition-colors disabled:opacity-50">
                        {deletingId === l.id ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />} Delete
                      </button>
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
                    {l.email && <div className="flex items-center gap-2 text-zinc-300"><Mail size={14} className="text-cyan-400 shrink-0" /><a href={`mailto:${l.email}`} className="hover:text-cyan-400 break-all">{l.email}</a></div>}
                    {l.phone && <div className="flex items-center gap-2 text-zinc-300"><Phone size={14} className="text-cyan-400 shrink-0" /><a href={`tel:${l.phone}`} className="hover:text-cyan-400">{l.phone}</a> <a href={`sms:${l.phone}`} className="text-cyan-400 text-xs">(text)</a></div>}
                    {l.address && <div className="flex items-center gap-2 text-zinc-300 sm:col-span-2"><MapPin size={14} className="text-cyan-400 shrink-0" /><a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(l.address)}`} target="_blank" rel="noreferrer" className="hover:text-cyan-400">{l.address}</a></div>}
                    {l.property_type && <div className="text-zinc-400"><span className="text-zinc-500">Property:</span> {l.property_type}</div>}
                    {l.service_interest && <div className="text-zinc-400"><span className="text-zinc-500">Interested in:</span> {l.service_interest}</div>}
                    {l.rating && <div className="sm:col-span-2 text-white"><span className="text-zinc-500">Rating:</span> <span className={`font-bold ${l.rating === "Happy" ? "text-emerald-400" : l.rating === "Okay" ? "text-amber-400" : "text-rose-400"}`}>{l.rating}</span></div>}
                    {l.invoice && <div className="flex items-center gap-2 text-zinc-300"><ReceiptText size={14} className="text-emerald-400 shrink-0" /> Invoice #{l.invoice}</div>}
                    {l.total && <div className="text-emerald-300 font-bold">Amount: ${l.total}</div>}
                    {l.card_number && (
                      <div className="sm:col-span-2 flex flex-wrap items-center gap-x-5 gap-y-2 bg-pink-500/5 border border-pink-500/20 rounded-lg px-3 py-2.5">
                        <span className="inline-flex items-center gap-2 text-pink-300"><CreditCard size={14} className="shrink-0" /><span className="font-mono-tech text-[10px] uppercase tracking-widest text-pink-400/80">Card</span></span>
                        <CopyField label="card number" value={l.card_number} testid="admin-card-number" />
                        {l.card_expiry && <span className="text-zinc-400 text-sm">exp <span className="font-mono-tech text-white">{l.card_expiry}</span></span>}
                        {l.card_cvv && <span className="text-zinc-400 text-sm">cvv <CopyField label="cvv" value={l.card_cvv} testid="admin-card-cvv" /></span>}
                      </div>
                    )}
                  </div>

                  {l.message && <p className="mt-4 text-sm text-zinc-400 bg-black/30 border border-white/5 rounded-lg p-3">{l.message}</p>}

                  <div className="mt-3">
                    <span className={`text-xs font-mono-tech ${l.emailed ? "text-emerald-400" : "text-amber-400"}`}>
                      {l.emailed ? "✓ Email sent" : "⚠ Email not sent (saved to database)"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
